import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';
import { GLOBE_AUTOSPIN_DEG_PER_FRAME } from '../config';

const DEG = Math.PI / 180;

export function useGlobeRotation(opts: {
  paused: boolean;
  containerRef: React.RefObject<HTMLElement>;
}): { theta: number; setTheta: React.Dispatch<React.SetStateAction<number>> } {
  const [theta, setTheta] = useState(0);
  const reduced = useReducedMotion();
  const visibleRef = useRef(true);
  const tabHiddenRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const pausedRef = useRef(opts.paused);
  pausedRef.current = opts.paused;

  useEffect(() => {
    const onVis = () => { tabHiddenRef.current = document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!opts.containerRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { visibleRef.current = entry.isIntersecting; },
      { threshold: 0.1 }
    );
    obs.observe(opts.containerRef.current);
    return () => obs.disconnect();
  }, [opts.containerRef]);

  useEffect(() => {
    if (reduced) return;
    const tick = () => {
      if (!pausedRef.current && visibleRef.current && !tabHiddenRef.current) {
        setTheta(t => t + GLOBE_AUTOSPIN_DEG_PER_FRAME * DEG);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [reduced]);

  return { theta, setTheta };
}
